import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { adminUserService, UserDetails as UserDetailsType } from '@/services/adminUserService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw } from 'lucide-react';

interface UserDetailSubscriptionProps {
  userDetails: UserDetailsType;
  onRefresh: () => void;
}

const UserDetailSubscription: React.FC<UserDetailSubscriptionProps> = ({ userDetails, onRefresh }) => {
  const [syncingSubscription, setSyncingSubscription] = useState(false);

  const handleSyncSubscription = async () => {
    if (!userDetails.id) return;
    try {
      setSyncingSubscription(true);
      await adminUserService.syncUserSubscription(userDetails.id);
      toastHelper.newMessage('success', 'Success', 'Subscription synced successfully from PayPal');
      onRefresh();
    } catch (error: any) {
      toastHelper.newMessage(
        'error',
        'Error',
        error.response?.data?.message || 'Failed to sync subscription from PayPal'
      );
    } finally {
      setSyncingSubscription(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Active Subscriptions</CardTitle>
            <CardDescription>Current subscription details</CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSyncSubscription}
            disabled={syncingSubscription}
          >
            {syncingSubscription ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Fetch from PayPal
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {userDetails.active_subscriptions.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead>Billing Cycle</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan ID</TableHead>
                  <TableHead>Subscription ID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Current</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userDetails.active_subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <Badge variant="outline">{sub.tier}</Badge>
                    </TableCell>
                    <TableCell>{sub.billing_cycle}</TableCell>
                    <TableCell>
                      <Badge variant={sub.subscription_status === 'active' ? 'default' : 'secondary'}>
                        {sub.subscription_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs break-all">{sub.paypal_plan_id}</TableCell>
                    <TableCell className="font-mono text-xs break-all">
                      {sub.subscription_id || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {sub.created_at
                        ? new Date(sub.created_at).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {sub.updated_at
                        ? new Date(sub.updated_at).toLocaleString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {sub.is_current ? (
                        <Badge variant="default">Current</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">No active subscriptions</p>
        )}
      </CardContent>
    </Card>
  );
};

export default UserDetailSubscription;

